import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8"><Logo size={40} /></div>
        <p className="overline mb-2">Password reset</p>
        <h2 className="font-display text-3xl text-white mb-6">Set a new password</h2>
        {!token ? (
          <p className="text-sm text-[#FF6666]" data-testid="reset-no-token">This reset link is missing its token. Request a new one.</p>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
            <div className="space-y-2">
              <Label htmlFor="password">New password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={10} placeholder="At least 10 characters" data-testid="reset-password-input" className="bg-secondary border-border h-11" />
            </div>
            {error && <div className="text-sm text-[#FF6666]" data-testid="reset-error">{error}</div>}
            <Button type="submit" disabled={loading} className="w-full h-11 bg-white text-black hover:bg-white/90 font-semibold" data-testid="reset-submit-button">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </form>
        )}
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-muted-foreground mt-6 hover:text-white transition-colors" data-testid="back-to-login-link">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
